import { camelizeResponse } from './keyMap'
import type { EventsListData } from './types'

const ENDPOINT = '/webshared/controllers/eventsList.cfc'

export class EventsListApiError extends Error {}

export interface EventsListQuery {
  search: string
  lat: number | null
  lng: number | null
  radiusMi: number | null
  when: 'any' | 'weekend' | '30days'
  /** Registration-state filter values: 'open' | 'soon' | 'full' */
  regState: string[]
  /** Racing-type filter values: 'offroad' | 'onroad' | 'oval' */
  raceType: string[]
  /** Real vehicletypecodes.typeName values (e.g. "1/10th Electric Truck") - see
   *  FilterOptions.vehicleType. */
  vehicleType: string[]
  seriesTrackId: number | null
  sort: 'soonest' | 'nearest' | 'mostEntries'
}

/** Read-only aggregate fetch - same GET-with-query-string shape as event-page's
 *  fetchEventPageData, since this is read-only (no form/body needed). Array-valued filters go in
 *  as comma-joined lists, matching the CFML convention <cfargument type="string" ... list> used
 *  throughout webshared/controllers (e.g. events.cfc's own list-typed arguments). */
export async function fetchEventsListData(query: EventsListQuery): Promise<EventsListData> {
  const params = new URLSearchParams({ method: 'getEventsListData' })
  if (query.search) params.set('search', query.search)
  if (query.lat != null) params.set('lat', String(query.lat))
  if (query.lng != null) params.set('lng', String(query.lng))
  if (query.radiusMi != null) params.set('radiusMi', String(query.radiusMi))
  params.set('when', query.when)
  if (query.regState.length) params.set('regState', query.regState.join(','))
  if (query.raceType.length) params.set('raceType', query.raceType.join(','))
  if (query.vehicleType.length) params.set('vehicleType', query.vehicleType.join(','))
  if (query.seriesTrackId != null) params.set('seriesTrackId', String(query.seriesTrackId))
  params.set('sort', query.sort)

  const res = await fetch(`${ENDPOINT}?${params.toString()}`, { credentials: 'same-origin' })
  if (!res.ok) {
    throw new EventsListApiError(`getEventsListData failed: HTTP ${res.status}`)
  }

  const raw = (await res.json()) as unknown
  const data = camelizeResponse<EventsListData>(raw)

  if (!data.success) {
    throw new EventsListApiError(data.error || 'getEventsListData returned success:false')
  }

  return data
}

/** Reads the SSR seed _eventsListGate.cfm embeds on window (null when absent), camelized the
 *  same way fetchEventsListData's AJAX response is. Lets useEventsListData seed its first render
 *  from real data instead of starting at null. */
export function getSeededEventsListData(): EventsListData | null {
  const raw = window.__RCS_EVENTS_LIST_DATA__
  if (!raw) return null
  const data = camelizeResponse<EventsListData>(raw)
  return data.success ? data : null
}
